<template>
    <div>
        <button
            v-if="!hideButton"
            @click="showModal = true"
            :class="[
                'btn-primary-sm text-sm flex items-center text-sm', 
                observationData ? 'btn-outline-white px-2' : 'btn-primary'
            ]"
            :loading="loading"
            size="sm"
        >
            <EyeIcon class="h-5 w-5 mr-1" />
            {{ observationData ? 'Ver detalhes' : buttonText }}
        </button>

        <TransitionRoot appear :show="showModal" as="template">
            <Dialog as="div" @close="closeModal" class="relative z-10">
                <TransitionChild
                    as="template"
                    enter="duration-300 ease-out"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="duration-200 ease-in"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="fixed inset-0 bg-black bg-opacity-25" />
                </TransitionChild>

                <div class="fixed inset-0 overflow-y-auto">
                    <div
                        class="flex min-h-full items-center justify-center p-4 text-center"
                    >
                        <TransitionChild
                            as="template"
                            enter="duration-300 ease-out"
                            enter-from="opacity-0 scale-95"
                            enter-to="opacity-100 scale-100"
                            leave="duration-200 ease-in"
                            leave-from="opacity-100 scale-100"
                            leave-to="opacity-0 scale-95"
                        >
                            <DialogPanel
                                class="w-full max-w-md dark:bg-gray-800 transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                            >
                                <DialogTitle
                                    as="h3"
                                    class="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                                >
                                    {{ observationData ? 'Detalhes da Observação' : 'Adicionar Usuário em Observação' }}
                                </DialogTitle>

                                <!-- Disclaimer quando está visualizando detalhes da observação -->
                                <div v-if="observationData" class="mt-2 mb-4 p-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md">
                                    <p class="text-sm text-red-600 dark:text-red-300 font-medium">
                                        <span class="flex items-center mb-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                            </svg>
                                            <strong>AVISO IMPORTANTE</strong>
                                        </span>
                                        Estas informações são para uso interno e <strong>não devem ser compartilhadas</strong> com o usuário.
                                    </p>
                                </div>

                                <div v-if="!observationData" class="mt-4">
                                    <textarea
                                        v-model="reason"
                                        class="w-full p-2 border rounded-md dark:bg-gray-800 dark:text-white"
                                        placeholder="Digite o motivo da observação"
                                        rows="4"
                                    ></textarea>
                                </div>

                                <div v-else class="mt-4">
                                    <div class="mb-3">
                                        <span class="text-sm font-semibold dark:text-gray-200">Data de início:</span>
                                        <p class="text-sm dark:text-white">{{ formatDate(observationData.insertedAt) }}</p>
                                    </div>
                                    
                                    <div class="mb-3">
                                        <span class="text-sm font-semibold dark:text-gray-200">Motivo:</span>
                                        <p class="mt-1 p-3 bg-gray-50 dark:bg-gray-700 rounded-md text-sm dark:text-white border border-gray-200 dark:border-gray-600">
                                            {{ observationData.reason }}
                                        </p>
                                    </div>
                                    
                                    <div v-if="observationData.createdBy" class="mb-3">
                                        <span class="text-sm font-semibold dark:text-gray-200">Adicionado por:</span>
                                        <p class="text-sm dark:text-white">ID: {{ observationData.createdBy }}</p>
                                    </div>

                                    <div v-if="observationData.type" class="mb-3">
                                        <span class="text-sm font-semibold dark:text-gray-200">Tipo:</span>
                                        <p class="text-sm dark:text-white">{{ observationData.type }}</p>
                                    </div>
                                </div>

                                <div class="mt-4 flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        :class="[
                                            'inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium',
                                            observationData 
                                                ? 'bg-blue-100 text-blue-900 hover:bg-blue-200 dark:bg-blue-800 dark:text-white dark:hover:bg-blue-700'
                                                : 'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-white'
                                        ]"
                                        @click="closeModal"
                                    >
                                        {{ observationData ? 'Entendi' : 'Fechar' }}
                                    </button>
                                    <button
                                        v-if="!observationData"
                                        type="button"
                                        class="dark:bg-yellow-500 dark:text-white inline-flex justify-center rounded-md border border-transparent bg-yellow-500 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-600"
                                        @click="submit"
                                        :disabled="loading"
                                    >
                                        {{ loading ? 'Enviando...' : 'Confirmar' }}
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script>
import { ref, computed } from 'vue'
import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
} from '@headlessui/vue'
import { EyeIcon } from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'

export default {
    components: {
        TransitionRoot,
        TransitionChild,
        Dialog,
        DialogPanel,
        DialogTitle,
        EyeIcon,
    },
    props: {
        userId: {
            type: Number,
            required: true,
        },
        buttonText: {
            type: String,
            default: 'Em observação',
        },
        observationData: {
            type: Object,
            default: null,
        },
        hideButton: {
            type: Boolean,
            default: false
        }
    },
    emits: ['fetch'],
    setup(props, { emit }) {
        const toast = useToast()
        const showModal = ref(false)
        const reason = ref('')
        const loading = ref(false)
        
        const closeModal = () => {
            showModal.value = false
            reason.value = ''
        }
        
        // Método para abrir o modal a partir de fora do componente
        const openModal = () => {
            showModal.value = true;
        }

        const formatDate = (dateString) => {
            if (!dateString) return 'Data não disponível'
            return new Date(dateString).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }

        const submit = async () => {
            if (!reason.value) {
                toast.error('Por favor, insira um motivo para a observação')
                return
            }

            loading.value = true
            try {
                const response = await axios.post('/api/user/observation', {
                    user_id: props.userId,
                    reason: reason.value,
                })

                if (response.data.status === 'success') {
                    toast.success('Usuário adicionado à observação com sucesso')
                    emit('fetch')
                    closeModal()
                } else {
                    toast.error(response.data.message || 'Erro ao adicionar usuário à observação')
                }
            } catch (error) {
                toast.error(
                    error.response?.data?.message ||
                        'Erro ao adicionar usuário à observação'
                )
            } finally {
                loading.value = false
            }
        }

        return {
            showModal,
            reason,
            loading,
            closeModal,
            submit,
            formatDate,
            openModal
        }
    },
}
</script> 